if (!customElements.get('alvez-collection')) {
  customElements.define('alvez-collection', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.dialog = this.querySelector('dialog');
      this.querySelectorAll('button[data-density]').forEach(button => {
        button.addEventListener('click', () => {
          this.dataset.density = button.dataset.density;
          this.querySelectorAll('button[data-density]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
        }, options);
      });
      this.querySelector('.archive__mobile-filter').addEventListener('click', () => {
        this.dialog.showModal();
        document.documentElement.classList.add('archive-filters-open');
      }, options);
      this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); }, options);
      this.dialog.addEventListener('close', () => document.documentElement.classList.remove('archive-filters-open'), options);
      this.querySelectorAll('form').forEach(form => {
        form.addEventListener('change', event => {
          if (!event.target.matches('input[type="checkbox"], input[type="range"], select')) return;
          this.filterRequest?.abort();
          clearTimeout(this.filterTimer);
          this.filterTimer = setTimeout(() => form.requestSubmit(), 180);
        }, options);
        form.addEventListener('submit', event => {
          event.preventDefault();
          // Empty price bounds should not become zero-value filters.
          form.querySelectorAll('input[type="number"]').forEach(input => { input.disabled = input.value === ''; });
          form.querySelectorAll('alvez-price-range').forEach(range => range.prepareSubmit?.());
          this.applyFilters(form);
        }, options);
      });
      window.addEventListener('popstate', () => window.location.reload(), options);
      this.querySelectorAll('.archive__desktop-filters details').forEach(details => {
        details.addEventListener('keydown', event => {
          if (event.key === 'Escape' && details.open) {
            details.open = false;
            details.querySelector('summary').focus();
          }
        }, options);
        details.addEventListener('toggle', () => {
          if (details.open) this.querySelectorAll('.archive__desktop-filters details').forEach(other => { if (other !== details) other.open = false; });
        }, options);
      });
      document.addEventListener('click', event => {
        this.querySelectorAll('.archive__desktop-filters details[open]').forEach(details => {
          if (!details.contains(event.target)) details.open = false;
        });
      }, options);
    }
    async applyFilters(form) {
      clearTimeout(this.filterTimer);
      this.filterRequest?.abort();
      this.filterRequest = new AbortController();
      const request = this.filterRequest;
      const url = new URL(form.action);
      url.search = new URLSearchParams(new FormData(form)).toString();
      // Re-enable endpoint inputs after serialization so later changes include them.
      this.querySelectorAll('alvez-price-range input[type="hidden"]').forEach(input => { input.disabled = false; });
      const sectionUrl = new URL(url);
      sectionUrl.searchParams.set('section_id', this.dataset.sectionId);
      const mobileOpen = this.dialog.open;
      const scope = mobileOpen ? this.dialog : this.querySelector('.archive__desktop-filters');
      const openFilters = [...scope.querySelectorAll('details[open]')].map(details => details.dataset.filterName);
      const focused = document.activeElement;
      const focusName = focused?.getAttribute('name');
      const focusValue = focused?.getAttribute('value');
      const focusLabel = focused?.getAttribute('aria-label');
      this.setAttribute('aria-busy', 'true');
      this.querySelector('[data-filter-status]').textContent = 'Updating products…';
      try {
        const response = await fetch(sectionUrl, { signal: request.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection unavailable');
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = page.querySelector('alvez-collection');
        if (!next) throw new Error('Collection missing');
        if (request.signal.aborted || !this.isConnected) return;
        next.dataset.density = this.dataset.density;
        next.querySelectorAll('[data-density]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.density === next.dataset.density)));
        if (mobileOpen) this.dialog.close();
        window.history.pushState({}, '', url);
        this.replaceWith(next);
        const nextScope = mobileOpen ? next.querySelector('dialog') : next.querySelector('.archive__desktop-filters');
        nextScope.querySelectorAll('details').forEach(details => { details.open = openFilters.includes(details.dataset.filterName); });
        if (mobileOpen) {
          next.querySelector('dialog').showModal();
          document.documentElement.classList.add('archive-filters-open');
        }
        const target = [...nextScope.querySelectorAll('input, select')].find(input => focusLabel
          ? input.getAttribute('aria-label') === focusLabel
          : focusName && input.name === focusName && input.getAttribute('value') === focusValue);
        if (target?.disabled && target.type === 'range') {
          target.closest('alvez-price-range').addEventListener('price-range-ready', () => target.focus({ preventScroll: true }), { once: true });
        } else if (target && !target.disabled) target.focus({ preventScroll: true });
        next.querySelector('[data-filter-status]').textContent = next.querySelector('.archive__count').textContent.trim();
      } catch (error) {
        if (error.name !== 'AbortError') window.location.assign(url);
      }
    }
    disconnectedCallback() {
      if (!this.events) return;
      clearTimeout(this.filterTimer);
      this.filterRequest?.abort();
      if (this.dialog.open) this.dialog.close();
      document.documentElement.classList.remove('archive-filters-open');
      this.events.abort();
      this.events = null;
    }
  });
}

if (!customElements.get('alvez-price-range')) {
  // Share one small, unfiltered request between the desktop and mobile controls.
  const boundsRequests = new Map();
  customElements.define('alvez-price-range', class extends HTMLElement {
    async connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.lower = this.querySelector('[data-price-lower]');
      this.upper = this.querySelector('[data-price-upper]');
      this.handles = [...this.querySelectorAll('input[type="range"]')];
      this.slider = this.querySelector('.archive-price__slider');
      this.status = this.querySelector('.archive-price__status');
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.dataset.currency || 'GBP', maximumFractionDigits: 2
      });
      this.handles.forEach((handle, index) => handle.addEventListener('input', () => {
        const value = Number(handle.value);
        this.update(index === 0 ? Math.min(value, Number(this.upper.value)) : Number(this.lower.value),
          index === 1 ? Math.max(value, Number(this.lower.value)) : Number(this.upper.value));
      }, options));
      window.addEventListener('pageshow', () => {
        this.lower.disabled = false;
        this.upper.disabled = false;
      }, options);
      const url = new URL(this.dataset.collectionUrl, window.location.origin);
      url.searchParams.set('sort_by', 'price-ascending');
      url.searchParams.set('section_id', 'alvez-price-bounds');
      const key = `${url.href}:${this.dataset.currency}`;
      if (!boundsRequests.has(key)) {
        boundsRequests.set(key, fetch(url, { credentials: 'same-origin' }).then(async response => {
          if (!response.ok) throw new Error('Price bounds unavailable');
          const document = new DOMParser().parseFromString(await response.text(), 'text/html');
          const bounds = document.querySelector('[data-price-bounds]');
          if (!bounds) throw new Error('Price bounds missing');
          const minimum = Number(bounds.dataset.min);
          const maximum = Number(bounds.dataset.max);
          if (!Number.isFinite(minimum) || !Number.isFinite(maximum) || maximum < minimum) throw new Error('Invalid price bounds');
          return { minimum, maximum };
        }).catch(error => { boundsRequests.delete(key); throw error; }));
      }
      try {
        const { minimum, maximum } = await boundsRequests.get(key);
        if (this.events?.signal.aborted || !this.isConnected) return;
        this.minimum = minimum;
        this.maximum = maximum;
        this.ready = true;
        [...this.handles, this.lower, this.upper].forEach(input => {
          input.min = minimum;
          input.max = maximum;
        });
        this.handles.forEach(handle => { handle.disabled = minimum === maximum; });
        this.slider.setAttribute('aria-busy', 'false');
        this.update(this.dataset.lowerActive === 'true' ? Number(this.lower.value) : minimum,
          this.dataset.upperActive === 'true' ? Number(this.upper.value) : maximum, false);
        if (minimum === maximum) this.status.textContent = `All products are ${this.money.format(minimum)}.`;
        this.dispatchEvent(new Event('price-range-ready'));
      } catch {
        if (!this.isConnected) return;
        this.slider.hidden = true;
        this.status.textContent = 'Price slider unavailable. Please refresh to try again.';
      }
    }
    update(lower, upper, synchronize = true) {
      const clamp = value => Math.round(Math.max(this.minimum, Math.min(this.maximum, value)) * 100) / 100;
      lower = clamp(lower);
      upper = Math.max(lower, clamp(upper));
      this.lower.value = lower;
      this.upper.value = upper;
      this.querySelector('[data-price-from]').textContent = this.money.format(lower);
      this.querySelector('[data-price-to]').textContent = this.money.format(upper);
      this.handles[0].value = lower;
      this.handles[1].value = upper;
      const span = this.maximum - this.minimum;
      const start = span ? (lower - this.minimum) / span * 100 : 0;
      const end = span ? (upper - this.minimum) / span * 100 : 100;
      this.style.setProperty('--price-start', `${start}%`);
      this.style.setProperty('--price-end', `${end}%`);
      this.handles[0].style.zIndex = start > 50 ? '3' : '2';
      this.handles[1].style.zIndex = start > 50 ? '2' : '3';
      this.handles[0].setAttribute('aria-valuemax', upper);
      this.handles[1].setAttribute('aria-valuemin', lower);
      this.handles.forEach((handle, index) => handle.setAttribute('aria-valuetext', this.money.format(index ? upper : lower)));
      if (synchronize) this.closest('alvez-collection')?.querySelectorAll('alvez-price-range').forEach(range => {
        if (range !== this && range.ready) range.update(lower, upper, false);
      });
    }
    prepareSubmit() {
      if (!this.ready) return;
      // Full collection bounds do not need URL parameters or active filter chips.
      this.lower.disabled = this.lower.value === '' || Number(this.lower.value) <= this.minimum;
      this.upper.disabled = this.upper.value === '' || Number(this.upper.value) >= this.maximum;
    }
    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
    }
  });
}
